import React, { useState, useEffect } from 'react';
import NavigationSidebar from './NavigationSidebar';
import UserMenu from './auth/UserMenu';
import './AppLayout.css';

interface AppLayoutProps {
  children: React.ReactNode;
  activeNavItem?: string;
  onNavItemClick?: (item: string) => void;
}

const AppLayout: React.FC<AppLayoutProps> = ({ 
  children, 
  activeNavItem = 'launchpad',
  onNavItemClick 
}) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved ? JSON.parse(saved) : true; // Default to collapsed
    } catch {
      return true; // Default to collapsed if localStorage fails
    }
  });
  
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('sidebarCollapsed', JSON.stringify(isSidebarCollapsed));
    } catch {
      // Ignore localStorage errors
    }
  }, [isSidebarCollapsed]);

  const handleToggleCollapse = () => {
    if (isMobile) {
      setIsMobileOpen(!isMobileOpen);
    } else {
      setIsSidebarCollapsed(!isSidebarCollapsed);
    }
  };

  const handleNavItemClick = (item: string) => {
    if (onNavItemClick) {
      onNavItemClick(item);
    }
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  const handleOverlayClick = () => {
    setIsMobileOpen(false);
  };

  return (
    <div className={`app-layout ${isSidebarCollapsed && !isMobile ? 'sidebar-collapsed' : ''}`}>
      <NavigationSidebar
        isCollapsed={isSidebarCollapsed && !isMobile}
        onToggleCollapse={handleToggleCollapse}
        activeItem={activeNavItem}
        onItemClick={handleNavItemClick}
      />
      
      {isMobile && (
        <>
          <button 
            className="mobile-menu-toggle"
            onClick={() => setIsMobileOpen(true)}
            aria-label="Open navigation menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12h18M3 6h18M3 18h18" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div 
            className={`sidebar-overlay ${isMobileOpen ? 'visible' : ''}`}
            onClick={handleOverlayClick}
          />
          <div className={`mobile-sidebar-wrapper ${isMobileOpen ? 'open' : ''}`}>
            <NavigationSidebar
              isCollapsed={false}
              onToggleCollapse={handleToggleCollapse}
              activeItem={activeNavItem}
              onItemClick={handleNavItemClick}
            />
          </div>
        </>
      )}
      
      <div className="app-content">
        <header className="app-header">
          <div className="app-header-content">
            <div className="app-header-left">
              {/* Space for breadcrumbs or page title if needed */}
            </div>
            <div className="app-header-right">
              <UserMenu />
            </div>
          </div>
        </header>
        <main className="app-main">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;